import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Wallet,
  CalendarRange,
  Banknote,
  Coffee,
  Users,
  FileBarChart2,
  LogOut,
  Menu,
  X,
  ChevronsUpDown,
  Search,
  BookOpen,
  FileInput,
  FileOutput,
  Library,
  UserCog,
  TrendingUp,
  FolderLock,
  ShieldCheck,
  Calendar as CalendarIcon,
  BellRing,
  FileSignature,
  Settings as Settings2Icon,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import CommandPalette from "@/components/CommandPalette";

/* ----------------------------- Nav config ----------------------------- */
// If `roles` omitted, item is visible to all roles.
const ALL_INSIDER = ["admin", "accounts", "partner"];  // hidden from investors
// Nav items to hide from scoped accountants (users with assigned_investment_ids).
// A scoped accountant sees only what applies to their tagged projects.
const HIDE_FROM_SCOPED_ACCT = new Set([
  "/market-valuation", "/partners", "/documents", "/loans", "/monthly-income",
]);
const SECTIONS = [
  {
    label: "Favourites",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/monthly-income", label: "Monthly Income", icon: CalendarRange, testid: "nav-monthly" },
      { to: "/market-valuation", label: "Market Valuation", icon: TrendingUp, testid: "nav-mtm" },
      { to: "/insights", label: "Insights", icon: FileBarChart2, testid: "nav-insights" },
    ],
  },
  {
    label: "Projects",
    items: [
      { to: "/investments", label: "Investments", icon: Wallet, testid: "nav-investments", countKey: "investments" },
      { to: "/performance", label: "Performance", icon: TrendingUp, testid: "nav-performance" },
      { to: "/loans", label: "Loan Tracker", icon: Banknote, testid: "nav-loans", roles: ALL_INSIDER },
      { to: "/cafe", label: "Cafe P&L", icon: Coffee, testid: "nav-cafe", roles: ALL_INSIDER },
    ],
  },
  {
    label: "Books",
    items: [
      { to: "/cash-book", label: "Cash & Bank", icon: BookOpen, testid: "nav-cashbook", roles: ALL_INSIDER },
      { to: "/bills", label: "Purchase Bills", icon: FileInput, testid: "nav-bills", roles: ALL_INSIDER },
      { to: "/invoices", label: "Sales Invoices", icon: FileOutput, testid: "nav-invoices", roles: ALL_INSIDER },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/compliance", label: "Policies", icon: ShieldCheck, testid: "nav-compliance", roles: ALL_INSIDER, end: true },
      { to: "/compliance/calendar", label: "Calendar", icon: CalendarIcon, testid: "nav-calendar", roles: ALL_INSIDER },
      { to: "/compliance/reminders", label: "Reminders", icon: BellRing, testid: "nav-reminders", badgeKey: "reminders" },
      { to: "/compliance/advices", label: "Payment Advices", icon: FileSignature, testid: "nav-advices", roles: ALL_INSIDER },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/partners", label: "Partners", icon: Users, testid: "nav-partners", countKey: "partners", roles: ALL_INSIDER },
      { to: "/masters", label: "Masters", icon: Library, testid: "nav-masters", roles: ["admin", "accounts"] },
      { to: "/documents", label: "Documents", icon: FolderLock, testid: "nav-documents", roles: ALL_INSIDER },
      { to: "/users", label: "Users", icon: UserCog, testid: "nav-users", roles: ["admin"] },
      { to: "/settings", label: "Settings", icon: Settings2Icon, testid: "nav-settings", roles: ["admin"] },
    ],
  },
];

// Filter sections for current role
const sectionsFor = (role, isScopedAcct = false) =>
  SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => {
      if (i.roles && !i.roles.includes(role)) return false;
      if (isScopedAcct && HIDE_FROM_SCOPED_ACCT.has(i.to)) return false;
      return true;
    }),
  })).filter((s) => s.items.length > 0);

/* 5 most-used pages, shown as an iOS-style bottom dock on mobile */
const DOCK_DEFAULT = [
  { to: "/", label: "Home", icon: LayoutDashboard },
  { to: "/monthly-income", label: "Income", icon: CalendarRange },
  { to: "/cash-book", label: "Cash", icon: BookOpen, roles: ALL_INSIDER },
  { to: "/investments", label: "Assets", icon: Wallet },
  { to: "/partners", label: "Partners", icon: Users, roles: ALL_INSIDER },
  { to: "/insights", label: "Insights", icon: FileBarChart2, roles: ["investor"] },
];
const dockFor = (role) => DOCK_DEFAULT.filter((i) => !i.roles || i.roles.includes(role)).slice(0, 5);

/* -------------------------- Small helpers UI -------------------------- */
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "U";

const TrafficLights = () => (
  <div className="flex items-center gap-1.5 px-4 pt-3.5">
    <span className="h-3 w-3 rounded-full bg-[#FF5F57] shadow-inner" />
    <span className="h-3 w-3 rounded-full bg-[#FEBC2E] shadow-inner" />
    <span className="h-3 w-3 rounded-full bg-[#28C840] shadow-inner" />
  </div>
);

const NavItem = ({ item, count, onClick }) => (
  <NavLink
    to={item.to}
    end={item.end || item.to === "/"}
    onClick={onClick}
    data-testid={item.testid}
    className={({ isActive }) =>
      `group relative mx-2 flex items-center gap-2.5 px-2.5 py-[7px] rounded-md text-[13px] transition-colors ${
        isActive
          ? "bg-red-600 text-white"
          : "text-slate-700 hover:bg-slate-200/60"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <item.icon
          className={`h-4 w-4 shrink-0 ${isActive ? "text-white" : "text-slate-500 group-hover:text-slate-700"}`}
          strokeWidth={1.75}
        />
        <span className="truncate font-medium tracking-[-0.005em]">{item.label}</span>
        {typeof count === "number" && count > 0 && (
          <span
            className={`ml-auto text-[10px] font-mono-fin tabular-nums px-1.5 py-[1px] rounded ${
              isActive
                ? item.badgeKey ? "bg-white text-red-600" : "bg-white/20 text-white"
                : item.badgeKey
                  ? "bg-red-600 text-white"
                  : "bg-slate-200 text-slate-600 group-hover:bg-slate-300"
            }`}
          >
            {count}
          </span>
        )}
      </>
    )}
  </NavLink>
);

const SectionHeader = ({ label }) => (
  <div className="px-4 pt-4 pb-1 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">
    {label}
  </div>
);

/* ----------------------------- Sidebar ------------------------------ */
const SidebarInner = ({ user, logout, counts, onNavClick, showTrafficLights }) => (
  <>
    {showTrafficLights && <TrafficLights />}

    {/* Brand */}
    <Link
      to="/"
      onClick={onNavClick}
      className="block mx-3 mt-3 mb-2 rounded-lg overflow-hidden bg-red-600 shrink-0"
    >
      <div className="px-3 py-2 flex items-center justify-center">
        <img
          src="/marksam-logo-white.png"
          alt="MARKSAM Ventures"
          className="h-14 w-auto max-w-full object-contain"
          data-testid="sidebar-logo"
        />
      </div>
    </Link>

    {/* Mock search (⌘K placeholder — visual only for now) */}
    <div className="mx-3 mb-2 shrink-0">
      <div className="flex items-center gap-2 h-7 px-2.5 rounded-md bg-white border border-slate-200/80 text-slate-400">
        <Search className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="text-[12px] flex-1">Quick jump</span>
        <span className="text-[10px] font-mono-fin border border-slate-200 px-1 rounded text-slate-400">⌘K</span>
      </div>
    </div>

    {/* Sections */}
    <nav className="flex-1 min-h-0 overflow-y-auto pb-3 thin-scroll">
      {sectionsFor(user?.role, user?.is_scoped && user?.role === "accounts").map((section) => (
        <div key={section.label}>
          <SectionHeader label={section.label} />
          <div className="space-y-[1px]">
            {section.items.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                count={item.badgeKey ? counts[item.badgeKey] : (item.countKey ? counts[item.countKey] : undefined)}
                onClick={onNavClick}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>

    {/* User chip footer */}
    <div className="shrink-0 mx-2 mb-2 rounded-lg bg-white/80 border border-slate-200/80 backdrop-blur-sm p-2 flex items-center gap-2.5">
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-[11px] font-semibold bg-gradient-to-br from-red-500 to-red-700 shrink-0"
        aria-hidden="true"
      >
        {initialsOf(user?.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="text-[12.5px] font-medium text-slate-900 truncate leading-tight"
          data-testid="current-user-name"
        >
          {user?.name}
        </div>
        <div className="text-[10.5px] text-slate-500 truncate leading-tight mt-0.5">
          {user?.email}
        </div>
      </div>
      <button
        onClick={logout}
        data-testid="logout-btn"
        title="Sign out"
        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-slate-100 transition-colors shrink-0"
      >
        <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    </div>

    {/* Bottom-most role chip */}
    <div className="shrink-0 px-4 pb-3 flex items-center justify-between">
      <span className="text-[9px] tracking-[0.24em] uppercase text-slate-400">
        {user?.role}
      </span>
      <ChevronsUpDown className="h-3 w-3 text-slate-300" strokeWidth={1.75} />
    </div>
  </>
);

/* ------------------------------ Layout ------------------------------- */
export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [counts, setCounts] = useState({ investments: 0, partners: 0, reminders: 0 });

  const refreshCounts = () => {
    Promise.all([
      api.get("/investments").catch(() => ({ data: [] })),
      api.get("/partners").catch(() => ({ data: [] })),
      api.get("/reminders?status=pending").catch(() => ({ data: [] })),
    ]).then(([inv, ptn, rem]) => {
      setCounts({ investments: inv.data.length, partners: ptn.data.length, reminders: rem.data.length });
    });
  };

  useEffect(() => {
    refreshCounts();
    const t = setInterval(refreshCounts, 5 * 60 * 1000);
    const onChange = () => refreshCounts();
    window.addEventListener("reminders-changed", onChange);
    return () => {
      clearInterval(t);
      window.removeEventListener("reminders-changed", onChange);
    };
  }, []);

  // Global ⌘K / Ctrl+K listener
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="h-screen bg-[#F4F1EA] flex overflow-hidden">
      {/* Desktop sidebar — frosted, always visible */}
      <aside
        className="hidden md:flex flex-col w-[248px] shrink-0 h-screen
                   bg-[#EDEAE1]/85 backdrop-blur-xl
                   border-r border-slate-300/40"
      >
        <SidebarInner
          user={user}
          logout={logout}
          counts={counts}
          onOpenCmd={() => setCmdOpen(true)}
          showTrafficLights
        />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-40 flex items-center justify-between px-4 h-14"
           style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <Link to="/" className="flex items-center gap-2">
          <div className="bg-red-600 rounded-md px-2 py-1">
            <img src="/marksam-logo-white.png" alt="MARKSAM" className="h-6 w-auto" />
          </div>
        </Link>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCmdOpen(true)}
            className="p-2 -mr-1 rounded-md hover:bg-slate-100"
            data-testid="cmdk-trigger-mobile"
            aria-label="Quick jump"
          >
            <Search className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 -mr-2 rounded-md hover:bg-slate-100"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — full nav */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-14 bg-[#EDEAE1] z-30 flex flex-col"
             style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <SidebarInner
            user={user}
            logout={logout}
            counts={counts}
            onOpenCmd={() => { setMobileOpen(false); setCmdOpen(true); }}
            onNavClick={() => setMobileOpen(false)}
          />
        </div>
      )}

      {/* Main content — the only scrollable region */}
      <main
        className="flex-1 min-w-0 h-screen overflow-y-auto md:pt-0 pt-14 pb-20 md:pb-0 thin-scroll bg-[#FAFAF7]"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 3.5rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 5rem)",
        }}
      >
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile dock — bottom tab bar (feels like a native app) */}
      <nav
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 flex items-stretch ${mobileOpen ? "hidden" : ""}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-dock"
      >
        {dockFor(user?.role).map((d) => (
          <NavLink
            key={d.to}
            to={d.to}
            end={d.to === "/"}
            data-testid={`dock-${d.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                isActive ? "text-red-600" : "text-slate-500 active:text-slate-900"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className={`p-1.5 rounded-lg transition-colors ${isActive ? "bg-red-50" : ""}`}>
                  <d.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2 : 1.75} />
                </div>
                <span className="tracking-[-0.005em]">{d.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} userRole={user?.role} />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
